import { formatDisplayEmail } from '@bs/core'
import { InitialsAvatar, pantryColors } from '@pantry/ui'
import React from 'react'
import type { LoyaltyMember } from '../../../../../types/loyalty'
import { useMembersContext } from '../../../hooks/useMembersContext'
import {
  avatarColorFromName,
  fullName,
  initialsFromName,
} from '../../../utils/avatar'
import {
  formatLastActivity,
  formatPoints,
  formatPointsAsDollars,
} from '../../../utils/format'
import { StatusDot } from '../../shared/StatusDot'
import { TierBadge } from '../../shared/TierBadge'

const p = pantryColors

const EMERALD = '#059669'
const AMBER = p.amber[700]

interface MemberRowProps {
  member: LoyaltyMember
  // Store redemption rate (pts = $1) used for the points-balance "$ value"
  // sub-line. Sourced from LoyaltyRules.redemption.rate via LoyaltyContext.
  redemptionRate?: string | number | null
}

const cellStyle: React.CSSProperties = {
  padding: '12px 14px',
  borderBottom: `1px solid ${p.gray[100]}`,
  fontSize: 13,
  verticalAlign: 'middle',
}

const rightCellStyle: React.CSSProperties = {
  ...cellStyle,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
}

export const MemberRow: React.FC<MemberRowProps> = ({
  member,
  redemptionRate,
}) => {
  const { openMember, selectedMember, drawerOpen } = useMembersContext()
  const name =
    fullName(member.firstName, member.lastName) || formatDisplayEmail(member.email) || '—'

  const isSelected =
    drawerOpen &&
    selectedMember?.email === member.email &&
    selectedMember?.firstName === member.firstName &&
    selectedMember?.lastName === member.lastName

  return (
    <tr
      onClick={() => openMember(member)}
      style={{
        cursor: 'pointer',
        background: isSelected ? p.gray[50] : 'transparent',
        transition: 'background .12s',
      }}
      onMouseEnter={(e) => {
        if (!isSelected) e.currentTarget.style.background = p.gray[50]
      }}
      onMouseLeave={(e) => {
        if (!isSelected) e.currentTarget.style.background = 'transparent'
      }}
    >
      <td style={cellStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <InitialsAvatar
            initials={initialsFromName(name)}
            color={avatarColorFromName(name)}
            size="sm"
          />
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontWeight: 600,
                color: p.gray[800],
                fontSize: 13,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {name}
            </div>
            <div
              style={{
                fontSize: 11.5,
                color: p.gray[400],
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {formatDisplayEmail(member.email)}
            </div>
          </div>
        </div>
      </td>
      <td style={cellStyle}>
        <TierBadge tier={member.loyaltyTier} />
      </td>
      <td style={rightCellStyle}>
        <div style={{ fontWeight: 700, color: p.gray[800] }}>
          {formatPoints(member.pointsBalance)}
        </div>
        <div style={{ fontSize: 11, color: p.gray[400] }}>
          ≈ {formatPointsAsDollars(member.pointsBalance, redemptionRate)}
        </div>
      </td>
      <td style={{ ...rightCellStyle, color: EMERALD, fontWeight: 600 }}>
        {formatPoints(member.lifetimeEarnedPoints)}
      </td>
      <td style={{ ...rightCellStyle, color: AMBER, fontWeight: 600 }}>
        {formatPoints(member.lifetimeRedeemPoints)}
      </td>
      <td style={{ ...cellStyle, fontSize: 12.5, color: p.gray[500] }}>
        {formatLastActivity(member.loyaltyLastActivity)}
      </td>
      <td style={cellStyle}>
        <StatusDot status={member.status} />
      </td>
    </tr>
  )
}
